import ResponseHelper from './response-helper';

/**
 * Lib that manages communication with Vizzto server related to user's account.
 * Returns with `code = 0` are considered sucessfull.
 */

/**
 * @typedef FieldErrorResponse All methods can return a failed promise with field validation errors following this structure.
 * @type {Object}
 * @property {Number} code Field validation error code (400)
 * @property {String} message Global error message (like: "Some fields are invalid.").
 * @property {Object} data Holds the errors. Each property is related to a field error. Property's key must be the field name. Property's value must be the error message.
 * @example { code: 400, message: "Request is invalid.", data: { user: "User cannot have more than 3 chars.", password: "Password doesn't have special char." } }
 */

/**
 * @typedef Address
 * @type {Object}
 * @property {String} zip Zip code digits (00000000)
 * @property {String} street
 * @property {String} [number]
 * @property {String} [extra] Address complement
 * @property {String} [district]
 * @property {String} city
 * @property {String} state 2-chars state identifier
 * @property {String} country 2-chars country identifier
 */

/**
 * @typedef UserData
 * @type {Object}
 * @property {String} name
 * @property {String} document CPF
 * @property {String} birthday Birthday digits (00000000)
 * @property {String} nationality
 * @property {String} phone Phone digits (00000000000)
 * @property {Address} address
 * @property {Object} email
 * @property {String} email.value The e-mail address
 * @property {Boolean} email.confirmed Whether e-mail address was validated
 */

function planMapper(item) {
  return {
    planOwnerId: item.planOwnerId,
    name: item.name,
    shortDescription: item.shortDescription,
    longDescription: item.longDescription,
    country: item.country,
    cost: item.cost,
    billPeriodicity: item.billPeriodicity,
    free: item.free,
    trialOn: item.trialOn,
    trialMonths: item.trialMonths,
    trialOnlyUserCreation: item.trialOnlyUserCreation,
    planId: item.planId,
    planStatus: item.planStatus,
    associatedPlans: item.associatedPlans,
    planChildren: item.planChildren,
    cancelDate: item.cancelDate
  };
}

function subscriptionMapper(item) {
  return {
    subscriptionId: item.subscriptionId,
    planOwnerId: item.planOwnerId,
    name: item.planName,
    shortDescription: item.planDescription,
    longDescription: item.planDescription,
    cost: item.planValue,
    trialOn: item.trial,
    planId: item.planId,
    associatedPlans: item.associatedPlanIds,
    planChildren: item.planChildren,
    billId: item.billId,
    cancelDate: item.cancelDate,
    planType: item.planType
  };
}

function userDataMapper(item) {
  const birthday = item.birthDate;
  return {
    name: item.name,
    socialName: item.socialName,
    document: item.personalIds[0]?.value,
    birthDate: birthday,
    birthday,
    nationality: item.nationality,
    phone: item.phone1,
    profilePicture: item.profilePicture,
    address: {
      zip: item.postalCode,
      street: item.addr ?? item.street ?? '',
      number: item.streetNumber ?? '',
      extra: item.complement ?? '',
      district: item.district ?? '',
      city: item.city,
      cityCode: item.cityCode,
      state: item.state,
      country: item.country
    },
    email: {
      value: item.email1,
      confirmed: true // TODO não ha tal informação na chamada
    },
    photo: '', // Url of user's avatar image. If there is none, return empty string.
    links: [
      // List of user's links, like social networks. Ex.: { id: 'linkedin', name: 'LinkedIn', url: 'https://linkedin.com/in/lanza' }
      { id: '', name: '', url: '' }
    ]
  };
}

function deviceMapper(item) {
  return {
    id: item.id,
    deviceManagementId: item.deviceManagementId,
    name: item.userDeviceName,
    description: item.deviceTypeDescription,
    status: item.status, // Possible status: ACTIVE / INACTIVE / EXCLUDED
    registerDate: item.registerDate,
    lastAccessDate: item.lastAccessDate,
    loggedIn: true,
    current: true
  };
}

export default (axios) => ({
  /**
   * Initiates the reset password process. Validates e-mail informed by user.
   * If document/cpf matches an valid user in system, it should send an e-mail with link to /reset-password/:hash
   * @param {Number} distributorId
   * @param {String} uname User uname aka e-mail
   * @returns {Promise<{code: Number, message: String}>}
   */
  initResetPasswordProcess(distributorId, uname) {
    return axios({
      method: 'post',
      url: `users/password/recover`,
      data: {
        distributorId,
        username: uname
      }
    })
      .then(function (response) {
        return Promise.resolve({
          data: {
            email: response.data.email
          },
          code: 0,
          message: 'OK, e-mail sent to user with link to /reset-password/:hash'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Validates given hash to reset user password.
   * User should have received that hash by e-mail in a link.
   * @param {String} distributorId
   * @param {String} hash
   * @returns {Promise<{code: number, message: string}>}
   */
  // TODO sera revista ao final do processo
  validateResetPasswordHash(distributorId, hash) {
    return Promise.resolve({
      code: 0,
      message: 'Ok, hash is valid. User can set a new password.'
    });
  },

  /**
   * Last step of reset password process. Sets user's new password.
   * @param {String} distributorId
   * @param {String} hash User's hash validated in _validateResetPasswordHash_ method.
   * @param {String} password The new password.
   * @returns {Promise<{code: number, message: string}>}
   */
  // TODO sera revisto ao final do processo
  finishResetPasswordProcess(distributorId, hash, password) {
    return Promise.resolve({
      code: 0,
      message: 'Ok, password for user with that hash is set.'
    });
  },

  changePassword(oldPassword, newPassword, userToken) {
    return axios({
      method: 'put',
      url: `users/password`,
      headers: {
        utoken: userToken
      },
      data: {
        oldPassword,
        newPassword
      }
    })
      .then(function (response) {
        return Promise.resolve({
          data: response,
          code: 0,
          message: 'Sua senha foi alterada.'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Gets available plans and their info that must be shown to user. Like title, price, features...
   * @see {@link https://swagger.io/}
   * @param {String} distributorId Distributor id
   * @returns {Promise} Resolved promise with plans information or rejected promise on error
   */
  getAvailablePlans(distributorId) {
    return axios({
      method: 'get',
      url: `plans?distributorId=${distributorId}`
    })
      .then(function (response) {
        const plans = [];
        if (response?.data?.length) {
          response.data.forEach((item) => {
            plans.push(planMapper(item));
          });
        }
        return Promise.resolve({
          data: plans,
          code: 0,
          message: 'OK'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Get info of user's current plan.
   * @param {String} uname User Name
   * @param {String} userToken
   * @returns {Promise<{code: number, data: {plan: {longDescription: string, country: string, cost: number, trialOn: boolean, trialMonths: number, associatedPlans: *[], trialOnlyUserCreation: boolean, planStatus: string, shortDescription: string, billPeriodicity: string, planOwnerId: number, name: string, planId: number, free: boolean, planChildren: *[]}}, message: string}>}
   */
  getUserPlan(uname, userToken) {
    return axios({
      method: 'get',
      url: `plans/subscriptions/${uname}`,
      headers: {
        utoken: userToken
      }
    })
      .then(function (response) {
        return Promise.resolve({
          data: subscriptionMapper(response.data),
          code: 0,
          message: 'OK'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Assign a new plan to user.
   * @param {String} uname User Name
   * @param {String} userToken
   * @param {String} idNewPlan Plano novo ID
   * @param {String} idPlan Plano antigo ID
   * @returns {Promise<{code: number, message: string}>}
   */
  updateUserPlan(uname, userToken, idNewPlan, idPlan) {
    return axios({
      method: 'post',
      url: `plans/signplan/exchange/${uname}/${idPlan}/${idNewPlan}`,
      headers: {
        utoken: userToken
      }
    })
      .then(function (response) {
        return Promise.resolve({
          data: subscriptionMapper(response.data),
          code: 0,
          message: 'Seu plano foi alterado com sucesso.'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  cancelUserPlan(subscriptionId, userToken) {
    return axios({
      method: 'post',
      url: `plans/sigplan/break`,
      headers: {
        utoken: userToken
      },
      data: {
        subscriptionId
      }
    })
      .then(function () {
        return Promise.resolve({
          data: {},
          code: 0,
          message:
            'Seu plano foi cancelado com sucesso. Você será redirecionado para a página inicial.'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  reactivateUserPlan(userToken) {
    return axios({
      method: 'post',
      url: `plans/signplan/reactivate`,
      headers: {
        utoken: userToken
      }
    })
      .then(function () {
        return Promise.resolve({
          data: {},
          code: 0,
          message: 'Seu plano foi reativado. Aproveite!'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  signupPlan(userToken, planId) {
    return axios({
      method: 'post',
      url: `plans/sign`,
      headers: {
        utoken: userToken
      },
      data: {
        plan: { planId }
      }
    })
      .then(function () {
        return Promise.resolve({
          data: {},
          code: 0,
          message: 'Parabéns! Você assinou seu plano.'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Checks hash to verify user's e-mail address.
   * After e-mail confirmed, user must perform login to continue.
   * @see {@link https://swagger.io/}
   * @param {String} distributorId
   * @param {String} hash Hash to confirm user email
   * @returns {Promise} Resolved promise with confirmation result or reject promise on error.
   */
  confirmEmail(distributorId, hash) {
    // Exemplo de validação: https://vspprod30-gateway.vizzto.com.br/users/activate/9uvboelir92fbrgald0bl6o8
    return axios({
      method: 'put',
      url: `users/activate/${hash}`
    })
      .then(function (response) {
        return Promise.resolve({
          data: response.data, // retorna um html, podemos alterar este html no back
          code: 0,
          message: 'OK'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Gets user's data.
   * @param {String} uname
   * @param {String} userToken
   * @returns {Promise<{code: number, data: UserData, message: string}>}
   */
  getUserData(userToken) {
    return axios({
      method: 'get',
      url: `users/profile`,
      headers: {
        utoken: userToken
      }
    })
      .then(function (response) {
        return Promise.resolve({
          data: userDataMapper(response.data),
          code: 0,
          message: 'OK'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Changes user data.
   * @param {String} distributorId
   * @param {String} userToken
   * @param {UserData} userData
   * @returns {Promise<{code: number, message: string}>}
   */
  updateUserData(distributorId, userToken, userData) {
    const birthDate = userData.birthDate ?? userData.birthday ?? null;
    return axios({
      method: 'put',
      url: `users`,
      headers: {
        utoken: userToken
      },
      data: {
        uname: userData.uname,
        email1: userData.email,
        name: userData.name,
        birthDate,
        gender: null, // We don't have this info
        language: userData.language,
        nationality: userData.nationality,
        socialName: userData.socialName,
        phone1: userData.phone,
        postalCode: userData.address.zip,
        city: userData.city,
        cityCode: userData.address?.cityCode,
        state: userData.state,
        country: userData.country,
        cityOfBirth: null, // We don't have this info
        personalIds: [
          {
            country: 'BR',
            idType: 'CPF',
            value: userData.document
          }
        ],
        addr: userData.address.street,
        streetNumber: userData.address.number,
        complement: userData.address.extra,
        district: userData.address.district,
        upwd: userData.password
      }
    })
      .then(function () {
        return Promise.resolve({
          data: {},
          code: 0,
          message: 'OK'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Initiates process of changing e-mail address. Server must send an e-mail to validate.
   * @param {String} distributorId
   * @param userToken
   * @param emailAddress
   * @returns {Promise<{code: number, message: string}>}
   */
  // TODO ja esta na chamada do updateUserData
  updateUserEmail(distributorId, userToken, emailAddress) {
    return Promise.resolve({
      code: 0,
      message:
        'OK, I sent a message with a hash link to users new e-mail address.'
    });
  },

  /**
   * Initiates process of changing the user's profile photo.
   * @param {File} photo
   * @param userToken
   * @returns {Promise<void>}
   */
  updateUserProfilePhoto(photo, userToken) {
    const formData = new FormData();

    formData.append('file', photo);

    return axios({
      method: 'post',
      url: 'users/viewer/profile',
      headers: {
        utoken: userToken
      },
      data: formData
    }).then(
      (res) => ({ data: res.data.url, code: 0, message: 'OK' }),
      (error) => ResponseHelper.getErrorResponse(error)
    );
  },

  /**
   * Lists user devices.
   * @param {String} userToken
   * @returns {Promise<{code: number, data: {devices: [{current: boolean, loggedIn: boolean, name: string, description: string, id: string}]}, message: string}>}
   */
  getUserDevices(userToken) {
    return axios({
      method: 'get',
      url: `devicemanagement`,
      headers: {
        utoken: userToken
      }
    })
      .then(function (response) {
        const devices = [];
        response.data.forEach((item) => {
          devices.push(deviceMapper(item));
        });
        return Promise.resolve({
          data: devices,
          code: 0,
          message: 'OK'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },

  /**
   * Logs out user from a device.
   * @param {String} status ACTIVE / INACTIVE / EXCLUDED
   * @param {String} userDeviceName
   * @param {String} userToken
   * @param {String} deviceManagementId
   * @returns {Promise<{code: number, message: string}>}
   */
  updateUserDevice(userToken, deviceManagementId, status, userDeviceName) {
    return axios({
      method: 'put',
      url: `devicemanagement`,
      headers: {
        utoken: userToken
      },
      data: {
        deviceManagementId,
        status,
        userDeviceName
      }
    })
      .then(function (response) {
        return Promise.resolve({
          data: response.data,
          code: 0,
          message: 'OK'
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  },
  /**
   * Get info document exists
   * @param {String} document User Document
   * @param {String} distributorId Distributor Id
   * @returns {Promise<{code: number, data: {}, message: string}>}
   */
  validateDocument(document, distributorId) {
    return axios({
      method: 'get',
      url: `users/personalid/CPF/${document}/${distributorId}/exists`
    })
      .then(function (response) {
        return Promise.resolve({
          data: response.data,
          code: 0,
          message: ''
        });
      })
      .catch(function (error) {
        return Promise.reject(ResponseHelper.getErrorResponse(error));
      });
  }
});
